import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/shared/Button";
import Modal from "components/shared/Modal";
import ModalActionButtons from "components/shared/ModalActionButtons";
import styles from "./UnsavedChangesConfirmModal.styles";

type TUnsavedChangesConfirmModalProps = {
  open: boolean;
  isNavigationTrigger?: boolean;
  isSaveProhibited?: boolean;
  content?: ReactNode;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => void;
};

const UnsavedChangesConfirmModal = ({
  open,
  isNavigationTrigger,
  isSaveProhibited,
  content,
  onCancel,
  onDiscard,
  onSave,
}: TUnsavedChangesConfirmModalProps) => {
  const { t } = useTranslation("common");

  const title = isNavigationTrigger
    ? t("unsavedChanges.titleLeavingPage")
    : t("unsavedChanges.title");

  const buttons = useMemo(
    () => [
      [
        <Button
          key="cancel"
          data-testid="unsaved-changes-cancel-button"
          data-qaid="unsaved-changes-cancel-button"
          onClick={onCancel}
        >
          {t("unsavedChanges.cancel")}
        </Button>,
        <Button
          key="discard"
          data-testid="discard-changes-button"
          data-qaid="discard-changes-button"
          variant="outlined"
          onClick={onDiscard}
        >
          {t("unsavedChanges.discardChanges")}
        </Button>,
        <Button
          key="save"
          data-testid="unsaved-changes-save-button"
          data-qaid="unsaved-changes-save-button"
          disabled={isSaveProhibited}
          variant="contained"
          onClick={onSave}
        >
          {t("unsavedChanges.saveChanges")}
        </Button>,
      ],
    ],
    [isSaveProhibited, onCancel, onDiscard, onSave, t],
  );

  return (
    <Modal
      open={open}
      data-testid="unsaved-changes-modal"
      data-qaid="unsaved-changes-modal"
      title={title}
      sx={styles.modal}
    >
      {content ?? t("unsavedChanges.body")}
      <ModalActionButtons buttons={buttons} />
    </Modal>
  );
};

export default UnsavedChangesConfirmModal;
